"use client";
import { Button } from "@/components/Button";
import { BackIcon, RecentIcon, SearchIcon } from "@/components/Icons";
import { Row } from "@/components/Stack";
import { Text } from "@/components/Text";
import {
  DialectsEnum,
  AppRoutesEnum,
  EventNamesEnum,
  headerNavigationItems,
  SUBSCRIPTION_STATUS,
  cookiesValues,
  CookiesEnum,
  FlutterOutgoingMessageType,
  URLQueryParamsEnum,
} from "@/constants/constants";
import { useSettingsCopies } from "@/context/StringsProvider";
import { useUserContext } from "@/context/UserProvider";
import { useAllContent } from "@/hooks/useAllContent";
import { useClientTranslations } from "@/hooks/useClientTranslations";
import { useDialect } from "@/hooks/useDialect";
import { useLocalStorageValue } from "@/hooks/useLocalStorage";
import { useSearch } from "@/hooks/useSearch";
import { useTrackingAnalytics } from "@/hooks/useTrackingAnalytics";
import { FuzzySearch } from "@/utils/FuzzySearch";
import Link from "next/link";
import { useParams, usePathname, useRouter } from "next/navigation";
import { useEffect, useRef, useState, memo } from "react";
import { AutoComplete, Option } from "./AutoComplete";
import { Box } from "./Box";
import { DialectSelector } from "./DialectSelector";
import { Logo } from "./Logo";
import { SettingsDrawer } from "./settings/SettingsDrawer";
import {
  addQueryParamsToURL,
  cn,
  getClientSideCookieValue,
  getDialect,
  getDefaultDialect,
  getPaywallLink,
  shouldRedirectToSubscriptionFlow,
  isTvEnv,
} from "@/utils/common.helpers";
import {
  FocusContext,
  useFocusable,
} from "@noriginmedia/norigin-spatial-navigation";
import { Img } from "./Img";
import { CategoriesDropdown } from "./CategoriesDropdown";
import { sendToFlutter } from "@/utils/flutterBridge";

export interface HeaderProps {
  settings?: boolean;
  dialect?: string;
  showBackIcon?: boolean;
  text?: string;
  hideLogo?: boolean;
  hideBorder?: boolean;
  hideBackground?: boolean;
  backIconClasses?: string;
  showSearchInput?: boolean;
  triggerBackOnSearchClear?: boolean;
  subscriptionStatus?: SUBSCRIPTION_STATUS;
  newHeaderHeight?: boolean; // increase header height for desktop
  shouldShowHeaderNavigation?: boolean;
  isSticky?: boolean;
  showDialectSelector?: boolean;
  backPressEventName?: EventNamesEnum;
  isFocusable?: boolean;
  showUserCulturePopup?: boolean;
  disableLogoLink?: boolean;
}

const HEADER_HEIGHT = 92;

const HeaderComponent = ({
  settings = false,
  dialect,
  showBackIcon = false,
  text,
  hideLogo = false,
  hideBorder = false,
  hideBackground = false,
  backIconClasses = "",
  showSearchInput = false,
  subscriptionStatus,
  triggerBackOnSearchClear = true,
  newHeaderHeight = false,
  shouldShowHeaderNavigation = false,
  isSticky: isPositionedSticky = false,
  showDialectSelector = false,
  backPressEventName,
  isFocusable = true,
  showUserCulturePopup = false,
  disableLogoLink = false,
}: HeaderProps) => {
  const headerClasses = [
    "w-full z-[1] flex justify-between items-center px-4 md:px-6 p-4 pb-3 relative overflow-visible",
    hideBorder ? "" : "border-b border-white border-opacity-[0.08]",
    hideBackground ? " md:!bg-transparent" : "",
    newHeaderHeight ? "md:py-6" : "",
  ].join(" ");
  const { ref: headerRef, focusKey } = useFocusable({
    focusKey: "header",
    focusable: isFocusable,
  });

  const gradientRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!isPositionedSticky) return;
    if (!headerRef.current) return;

    const header = headerRef.current as HTMLElement;
    const isDesktop = window.innerWidth >= 768;

    header.style.transition = "background-color 300ms ease";

    const scrollHandler = () => {
      const scrollPos = window.scrollY;
      const gradient = gradientRef.current;

      if (scrollPos > HEADER_HEIGHT) {
        // Scrolled: solid black
        header.style.backgroundColor = "black";
        if (gradient) gradient.style.opacity = "0";
      } else {
        // At top: transparent header
        header.style.backgroundColor = "transparent";
        // Show gradient scrim on desktop only
        if (gradient && isDesktop) gradient.style.opacity = "1";
      }
    };

    scrollHandler();
    document.addEventListener("scroll", scrollHandler, { passive: true });

    return () => {
      document.removeEventListener("scroll", scrollHandler);
    };
  }, [isPositionedSticky]);

  return (
    <FocusContext.Provider value={focusKey}>
      <header
        className={headerClasses + " *:h-full "}
        data-testid="header"
        ref={headerRef}
      >
        {/* Desktop gradient scrim: covers header area and extends below to fade into the hero.
            Uses a tall div that overflows the header, creating the Netflix-style top gradient. */}
        {isPositionedSticky && (
          <div
            ref={gradientRef}
            className="pointer-events-none absolute inset-x-0 top-0 -z-[1] hidden md:block"
            style={{
              height: "250%",
              background:
                "linear-gradient(to bottom, rgba(0, 0, 0, 0.4) 0%, rgba(0, 0, 0, 0.2) 40%, transparent 100%)",
              transition: "opacity 300ms ease",
            }}
          />
        )}
        <HeaderLeft
          showBackIcon={showBackIcon}
          text={text}
          hideLogo={hideLogo}
          dialect={dialect}
          backIconClasses={backIconClasses}
          shouldShowHeaderNavigation={false}
          showDialectSelector={showDialectSelector}
          backPressEventName={backPressEventName}
          showUserCulturePopup={showUserCulturePopup}
          disableLogoLink={disableLogoLink}
        />
        {shouldShowHeaderNavigation && (
          <div className="absolute left-1/2 top-1/2 hidden !h-auto -translate-x-1/2 -translate-y-1/2 lg:block">
            <HeaderNavigation />
          </div>
        )}
        {((settings && dialect) || showSearchInput) && (
          <div className="flex flex-1 flex-col !items-end !justify-center">
            <HeaderRight
              dialect={dialect}
              showSearchInput={showSearchInput}
              showSettingsIcon={settings}
              triggerBackOnSearchClear={triggerBackOnSearchClear}
              subscriptionStatusProp={subscriptionStatus}
            />
          </div>
        )}
      </header>
    </FocusContext.Provider>
  );
};

const HeaderRight = ({
  dialect,
  showSearchInput,
  showSettingsIcon,
  triggerBackOnSearchClear,
  subscriptionStatusProp,
}: {
  dialect?: string;
  showSearchInput: boolean;
  showSettingsIcon: boolean;
  triggerBackOnSearchClear: boolean;
  subscriptionStatusProp?: SUBSCRIPTION_STATUS;
}) => {
  const { t } = useClientTranslations();
  const { currentLanguage } = useClientTranslations();
  const { dialect: paramDialect } = useParams();
  const {
    isTrialEligibleUser,
    hasActiveAccess,
    isSubscriptionStatusLoading,
    isFetchingCurrentProfile,
  } = useUserContext();
  const router = useRouter();
  const pathname = usePathname();
  const [isSettingsOpen, setIsSettingsOpen] = useState(false);
  const { fetchProfiles, isLoggedIn, selectedProfile } = useUserContext();
  const { currentDialect } = useDialect();

  const { ref: settingsButtonRef } = useFocusable({
    focusKey: "settings-button",
    onEnterPress: () => {
      handleSettingClick();
    },
  });

  const handleSettingClick = () => {
    if (paramDialect) {
      sessionStorage.setItem("previousDialect", paramDialect.toString());
    }
    setIsSettingsOpen(true);
  };

  let savedDialect = dialect;
  if (!savedDialect) {
    savedDialect = currentDialect.dialect.toLowerCase();
  }

  const isSearchPage = pathname?.includes("search");

  const paywallLink = getPaywallLink(
    isTrialEligibleUser,
    shouldRedirectToSubscriptionFlow(),
    currentLanguage,
    dialect,
  );

  return (
    <div
      className={`transition-all duration-500 md:opacity-0 ${!isSubscriptionStatusLoading && !isFetchingCurrentProfile ? "md:!opacity-100" : ""}`}
    >
      <Box width={["w-full", "md:w-auto"]} height="h-full">
        <Row
          gap={2}
          align="center"
          fullWidth={true}
          justify="end"
          fullHeight={true}
        >
          {/* we don't want to show the search input on the search page on tv */}
          {isSearchPage && isTvEnv() ? null : (
            <Search
              dialect={savedDialect}
              showSearchInput={showSearchInput}
              triggerBackOnSearchClear={triggerBackOnSearchClear}
            />
          )}

          {isLoggedIn ? (
            <>
              {!hasActiveAccess && (
                <Link
                  href={paywallLink}
                  className="stg-start-trial-gradient group hidden h-11 items-center justify-center rounded-[2.1875rem] px-7 py-2 md:flex"
                  data-testid="start-trial-button"
                >
                  <span className="transition-transform duration-200 group-hover:scale-[1.04]">
                    <Text
                      weight="bold"
                      size={2}
                      dangerouslyDisableDefaultTextColor
                    >
                      {isTrialEligibleUser &&
                      !shouldRedirectToSubscriptionFlow()
                        ? t("home_page.header_right.start_trial")
                        : t("home_page.header_right.purchase_subscription")}
                    </Text>
                  </span>
                </Link>
              )}
              {showSettingsIcon && (
                <button
                  ref={settingsButtonRef}
                  data-testid="settings-button"
                  onClick={handleSettingClick}
                  className="glass hidden h-11 w-11 items-center justify-center rounded-full md:flex"
                >
                  {selectedProfile?.avatar ? (
                    <Img
                      src={`${process.env.NEXT_PUBLIC_CDN_ROOT_URL}${selectedProfile.avatar}`}
                      alt={selectedProfile?.displayName}
                      className="rounded-full"
                      width={44}
                      height={44}
                      priority={true}
                    />
                  ) : (
                    <svg
                      width="20"
                      height="20"
                      viewBox="0 0 24 24"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <circle cx="12" cy="8" r="4" fill="white" />
                      <path
                        d="M5 20c0-3.866 3.134-7 7-7s7 3.134 7 7"
                        fill="white"
                      />
                    </svg>
                  )}
                </button>
              )}
            </>
          ) : (
            <>
              <Link
                href={`/${currentLanguage}/login`}
                className="hidden h-11 items-center justify-center rounded-[2.1875rem] px-7 py-2 text-sm font-bold text-white transition-colors duration-200 hover:bg-white/10 hover:text-white md:flex"
                data-testid="header-login-button"
              >
                {t("auth_screen.login")}
              </Link>
              <Link
                href={
                  !!dialect
                    ? paywallLink
                    : `${AppRoutesEnum.LOGIN}?redirectionAfterLoginTo=${paywallLink}`
                }
                className="stg-start-trial-gradient group hidden h-11 items-center justify-center rounded-[2.1875rem] px-7 py-2 md:flex"
                data-testid="start-trial-button"
              >
                <span className="transition-transform duration-200 group-hover:scale-[1.04]">
                  <Text
                    weight="bold"
                    size={2}
                    dangerouslyDisableDefaultTextColor
                  >
                    {t("home_page.header_right.start_trial")}
                  </Text>
                </span>
              </Link>
            </>
          )}
        </Row>
        {isSettingsOpen && (
          <SettingsDrawer onClose={() => setIsSettingsOpen(false)} />
        )}
      </Box>
    </div>
  );
};

const Search = ({
  dialect = DialectsEnum.HARYANVI,
  showSearchInput,
  triggerBackOnSearchClear,
}: {
  dialect?: string;
  showSearchInput: boolean;
  triggerBackOnSearchClear: boolean;
}) => {
  const pathname = usePathname();
  const isSearchPage = pathname?.includes("search");
  const router = useRouter();
  const { ref: searchButtonRef } = useFocusable({
    focusKey: "search-button",
    onEnterPress: () => {
      router.push(`/${dialect}/search`);
    },
  });

  if (isTvEnv()) {
    return (
      <Button
        className="mr-4"
        ref={searchButtonRef}
        noBg={true}
        icon={<SearchIcon />}
        href={`/${dialect}/search`}
      />
    );
  }

  if (!showSearchInput) return;

  return (
    <div className={isSearchPage ? "flex-1 lg:hidden" : "hidden"}>
      <SearchInput
        dialect={dialect}
        triggerBackOnSearchClear={triggerBackOnSearchClear}
      />
    </div>
  );
};

const HeaderLeft = ({
  showBackIcon,
  text,
  hideLogo,
  backIconClasses = "",
  dialect,
  shouldShowHeaderNavigation,
  showDialectSelector,
  backPressEventName,
  showUserCulturePopup,
  disableLogoLink = false,
}: Omit<HeaderProps, "settings" | "hideBorder" | "hideBackground">) => {
  const router = useRouter();
  const pathname = usePathname();
  const { trackEvent } = useTrackingAnalytics();
  const isWebview =
    getClientSideCookieValue(cookiesValues[CookiesEnum.IsWebview]) === "true";

  const handleBack = async () => {
    if (isWebview) {
      sendToFlutter(FlutterOutgoingMessageType.CLOSE);
    }
    if (backPressEventName) {
      await trackEvent({
        eventName: backPressEventName,
        properties: {},
        sendTo: [],
      });
    }

    const urlParams = new URLSearchParams(window.location.search);
    const redirectOnBack = urlParams.get("redirectOnBack");

    if (pathname && pathname.includes("settings")) {
      // Fixes a bug where changing the dialect from settings and then pressing the back button in the header
      // would navigate to the previous dialect's homepage. Previously, we used router.back(), which caused this issue.
      // it ensures that the homepage of the newly selected dialect is shown instead.
      const newDialect = await getDialect();
      const previousDialect = sessionStorage.getItem("previousDialect");
      if (!previousDialect || previousDialect === newDialect) {
        router.back();
      } else {
        sessionStorage.removeItem("previousDialect");
        router.push(`/${newDialect}`);
      }
    } else if (redirectOnBack && redirectOnBack !== "") {
      router.replace(decodeURIComponent(redirectOnBack));
    } else {
      router.back();
    }
  };

  return (
    <>
      {(showBackIcon || text) && (
        <Row fullHeight align="center">
          {showBackIcon ? (
            <Button
              className="mr-4"
              noBg={true}
              icon={<BackIcon className={backIconClasses} />}
              dataTestId="header-left-back-btn"
              onClick={handleBack}
            />
          ) : null}
          {text && <p className="ml-2 text-base font-bold">{text}</p>}
        </Row>
      )}
      <div className="relative flex shrink-0 items-center gap-16">
        <Row gap={2} fullHeight align="center" justify="center">
          {!hideLogo && (
            <div className="h-10 md:h-11">
              <Logo wrapInLink={!disableLogoLink} />
            </div>
          )}
          {showDialectSelector && (
            <DialectSelector
              dialect={dialect || ""}
              showUserCulturePopup={showUserCulturePopup}
            />
          )}
        </Row>
        {shouldShowHeaderNavigation && <HeaderNavigation />}
      </div>
    </>
  );
};

const SearchInput = ({
  dialect,
  triggerBackOnSearchClear,
}: {
  dialect: string;
  triggerBackOnSearchClear: boolean;
}) => {
  const { t } = useClientTranslations();
  const { trackEvent } = useTrackingAnalytics();
  const { isLoggedIn } = useUserContext();
  const { fetchAllContent, allContent } = useAllContent();
  const { setSearchDisplayedSuggestion } = useSearch();
  const [inputValue, setInputValue] = useState("");
  const [prevPage, setPrevPage] = useState<string>();
  const [isRecentSearchOptions, setIsRecentSearchOptions] = useState(false);
  const {
    value: recentSearches,
    setLocalStorageValue: setRecentSearches,
    getLocalStorageValue,
  } = useLocalStorageValue("recentSearches", true, []);
  const [autocompleteOptions, setAutocompleteOptions] = useState<
    {
      label: string;
      value: string;
    }[]
  >([]);
  const router = useRouter();
  const [autoCompleteSearchItems, setAutoCompleteSearchItems] = useState<
    string[]
  >([]);
  const [isFocused, setIsFocused] = useState(false);
  const { settingsCopies } = useSettingsCopies();
  const pathname = usePathname();
  const isSearchPage = pathname?.includes("search");

  const hints = (settingsCopies?.search_screen?.textFieldLabel ?? []).map(
    (item) => {
      let hint = item;
      if (hint.length > 10) {
        // If hint is too long, trim it to 10 chars. CSS text-overflow is not used since it would move the closing quote to the end and remove it
        hint = hint.substring(0, 10).trim() + "...";
      }
      return t("search.text_field_label", [hint]);
    },
  );

  const fuzzySearch = new FuzzySearch();

  useEffect(() => {
    if (!allContent) return;
    setAutoCompleteSearchItems(allContent?.map((item) => item.title) || []);
  }, [allContent]);

  const saveRecentSearch = (searchTerm: string) => {
    if (!searchTerm) return;
    const updatedSearches = [
      searchTerm,
      ...recentSearches.filter((item: string) => item !== searchTerm),
    ].slice(0, 4); // Keep only the 4 most recent searches
    setRecentSearches(updatedSearches);
  };

  const handleInputChange = (selectedOption: {
    label: string;
    value: string;
  }) => {
    const value = selectedOption.value;
    setInputValue(value);
    handleSearch(value);

    if (value === "") {
      setAutocompleteOptions(
        recentSearches.map((item: string) => ({
          label: item,
          value: item,
        })),
      );
      setIsRecentSearchOptions(true);
      if (triggerBackOnSearchClear) {
        router.replace(prevPage || `/${dialect}`);
      } else {
        router.replace(`/${dialect}/search`);
      }
      return;
    }

    const results = fuzzySearch.bestMatches(value, autoCompleteSearchItems, 4);
    if (results.length === 0) {
      setAutocompleteOptions(recentSearches);
      setIsRecentSearchOptions(true);
    } else {
      setAutocompleteOptions(
        results?.map((item) => ({
          label: item,
          value: item,
        })),
      );
      setIsRecentSearchOptions(false);
    }
  };

  const handleSearch = (query: string) => {
    const currentUrl = `/${dialect}/search?q=${encodeURIComponent(query)}`;
    if (isRecentSearchOptions) {
      setSearchDisplayedSuggestion([]);
    } else {
      setSearchDisplayedSuggestion(
        autocompleteOptions.map((item) => item.value),
      );
    }

    if (window.location.href.includes(`/${dialect}/search`)) {
      router.replace(currentUrl); // Replace the current URL without adding a new history entry
    } else {
      setPrevPage(window.location.href);
      // First-time search (i.e. open search page)
      router.push(currentUrl);
    }
  };

  useEffect(() => {
    fetchAllContent();
    setAutocompleteOptions(
      JSON.parse(getLocalStorageValue() || "[]").map((item: string) => ({
        label: item,
        value: item,
      })),
    );
    setIsRecentSearchOptions(true);
  }, []);
  const handleFocus = () => {
    setIsFocused(true);
    if (!isLoggedIn) {
      router.push(`/${dialect}/login`);
      return;
    }
    trackEvent({
      eventName: EventNamesEnum.SEARCH_INITIATED,
      properties: {
        search_stage: "tap",
        search_mode: "search_bar",
      },
    });
  };

  const handleManualSearch = (value: Option) => {
    saveRecentSearch(value.value);
    handleInputChange(value);
  };

  const handleOptionSelect = (value: Option) => {
    const eventName = EventNamesEnum.SEARCH_INITIATED;
    const searchMode = isRecentSearchOptions ? "recent" : "suggestion";
    const displayedOptions = autocompleteOptions
      .map((item, index) => `${index + 1}_${item.value}`)
      .join("_");

    // Build event properties dynamically
    const eventProperties = {
      search_stage: "searching",
      search_mode: searchMode,
      search_initiated_keyword: value.value,
      ...(isRecentSearchOptions
        ? { recent_searches_displayed: displayedOptions }
        : { suggestions_displayed: displayedOptions }),
    };

    // Track the event
    trackEvent({
      eventName,
      properties: eventProperties,
    });
  };

  const handleInputTyped = (value: Option) => {
    trackEvent({
      eventName: EventNamesEnum.SEARCH_INITIATED,
      properties: {
        search_stage: "searching",
        search_initiated_keyword: value.value,
      },
    });
    handleInputChange(value);
  };

  return (
    <div
      className={cn(
        "relative h-9 w-full rounded-lg border border-white/20 md:max-w-56",
        isSearchPage ? "bg-stg-secondary-cta" : isFocused && "bg-stg-grey-0",
      )}
      data-testid="search-input"
    >
      <AutoComplete
        options={autocompleteOptions}
        placeholder={t("search.hint_for_searching")}
        onValueChange={handleManualSearch}
        onEnter={handleManualSearch}
        value={{
          label: inputValue,
          value: inputValue,
        }}
        onFocus={handleFocus}
        onBlur={() => setIsFocused(false)}
        disabled={false}
        onInputChange={handleInputTyped}
        onOptionSelect={handleOptionSelect}
        showNoResult={false}
        icon={<SearchIcon width={18} height={18} />}
        showSelectedItem={false}
        listItemLeftIcon={isRecentSearchOptions ? <RecentIcon /> : null}
        animatedHints={isTvEnv() ? [] : hints}
        hasBlackBackground={!isSearchPage}
      />
    </div>
  );
};

export const Header = memo(HeaderComponent);

export const HeaderNavigation = () => {
  const { currentLanguage, t } = useClientTranslations();
  const { currentDialect } = useDialect();
  const { isLoggedIn } = useUserContext();
  const dialect = currentDialect.dialect.toLowerCase();
  const pathname = usePathname();
  const router = useRouter();
  const isCommonHome = pathname === AppRoutesEnum.COMMON_HOME;
  const defaultDialect = getDefaultDialect().toLowerCase();
  const navigationDialect = isCommonHome ? defaultDialect : dialect;
  const dialectHomeHref = `/${currentLanguage}/${dialect}`;
  const homeHref = isCommonHome
    ? AppRoutesEnum.COMMON_HOME
    : `/${currentLanguage}/${navigationDialect}`;
  const isHome =
    pathname === AppRoutesEnum.COMMON_HOME ||
    pathname === dialectHomeHref ||
    pathname === `${dialectHomeHref}/`;
  const isSearchPage = pathname?.includes("/search");

  const [isSearchMode, setIsSearchMode] = useState(false);
  const [searchValue, setSearchValue] = useState("");
  const [isNavTransitioning, setIsNavTransitioning] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  // Enter search mode when landing on search page
  useEffect(() => {
    if (isSearchPage) {
      setIsSearchMode(true);
      // Extract existing query from URL
      const params = new URLSearchParams(window.location.search);
      const q = params.get("q") || "";
      setSearchValue(q);
    } else {
      setIsSearchMode(false);
      setSearchValue("");
    }
  }, [isSearchPage]);

  // Track transition state to toggle overflow on categories wrapper
  useEffect(() => {
    setIsNavTransitioning(true);
    const timer = setTimeout(() => setIsNavTransitioning(false), 500);
    return () => clearTimeout(timer);
  }, [isSearchMode]);

  // Auto-focus input when entering search mode
  useEffect(() => {
    if (isSearchMode) {
      // Small delay to allow animation to start
      const timer = setTimeout(() => inputRef.current?.focus(), 300);
      return () => clearTimeout(timer);
    }
  }, [isSearchMode]);

  const handleSearchIconClick = () => {
    if (!isSearchMode) {
      if (!isLoggedIn) {
        router.push(
          `/${currentLanguage}/login?redirectionAfterLoginTo=/${navigationDialect}/search`,
        );
        return;
      }
      setIsSearchMode(true);
      router.push(`/${currentLanguage}/${navigationDialect}/search`);
    }
  };

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setSearchValue(value);
    if (value) {
      router.replace(
        `/${currentLanguage}/${navigationDialect}/search?q=${encodeURIComponent(
          value,
        )}`,
      );
    } else {
      router.replace(`/${currentLanguage}/${navigationDialect}/search`);
    }
  };

  const handleSearchKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Escape") {
      handleCloseSearch();
    }
  };

  const handleCloseSearch = () => {
    setIsSearchMode(false);
    setSearchValue("");
    router.push(homeHref);
  };

  return (
    <div className="hidden items-center lg:flex" data-testid="header-nav-items">
      <nav className="glass-nav flex h-[55px] items-center rounded-[100px] px-1.5 py-1.5">
        {/* Search icon */}
        <button
          onClick={handleSearchIconClick}
          className="flex h-[43px] w-[43px] shrink-0 items-center justify-center rounded-full text-white hover:text-white/80"
        >
          <SearchIcon width={20} height={20} />
        </button>

        {/* Morphing content area — nav items or search input */}
        <div className="relative flex items-center">
          {/* Search input layer */}
          <div
            className="flex items-center overflow-hidden"
            style={{
              width: isSearchMode ? 360 : 0,
              transition: `width ${isSearchMode ? "500ms" : "400ms"} cubic-bezier(0.4, 0, 0.2, 1)`,
            }}
          >
            <div
              className="flex w-[360px] items-center"
              style={{
                opacity: isSearchMode ? 1 : 0,
                transition: `opacity ${isSearchMode ? "300ms" : "200ms"} ease`,
                transitionDelay: isSearchMode ? "200ms" : "0ms",
              }}
            >
              <input
                ref={inputRef}
                type="text"
                value={searchValue}
                onChange={handleSearchChange}
                onKeyDown={handleSearchKeyDown}
                placeholder={t("search.hint_for_searching")}
                className="h-[43px] w-full bg-transparent px-2 text-lg font-light text-white placeholder-white/40 outline-none"
              />
              <button
                onClick={handleCloseSearch}
                className="mr-2 flex h-8 w-8 shrink-0 items-center justify-center rounded-full text-white/60 transition-colors hover:text-white"
              >
                <svg
                  width="16"
                  height="16"
                  viewBox="0 0 24 24"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    d="M18 6L6 18M6 6l12 12"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                  />
                </svg>
              </button>
            </div>
          </div>

          {/* Nav items layer */}
          <div
            className="flex items-center gap-1 overflow-hidden"
            style={{
              maxWidth: isSearchMode ? 0 : 600,
              opacity: isSearchMode ? 0 : 1,
              transition: isSearchMode
                ? "max-width 400ms cubic-bezier(0.4, 0, 0.2, 1), opacity 200ms ease"
                : "max-width 500ms cubic-bezier(0.0, 0, 0.2, 1) 200ms, opacity 400ms ease 250ms",
            }}
          >
            <Link
              className={cn(
                "whitespace-nowrap rounded-full px-5 py-2 text-sm font-medium transition-colors duration-200",
                isHome
                  ? "bg-white text-stg-grey-2"
                  : "text-white hover:bg-white/10 hover:text-white",
              )}
              href={homeHref}
            >
              {t("common.home")}
            </Link>
            {headerNavigationItems.map((item) => {
              const targetPath = `/${currentLanguage}/${navigationDialect}${item.href}`;
              const href = isCommonHome
                ? addQueryParamsToURL(targetPath, {
                    [URLQueryParamsEnum.SHOW_USER_CULTURE_POPUP]: true,
                  })
                : targetPath;
              const isActive = pathname === targetPath;
              return (
                <Link
                  className={cn(
                    "whitespace-nowrap rounded-full px-5 py-2 text-sm font-medium transition-colors duration-200",
                    isActive
                      ? "bg-white text-stg-grey-2"
                      : "text-white hover:bg-white/10 hover:text-white",
                  )}
                  href={href}
                  key={item.href}
                >
                  {t(item.label)}
                </Link>
              );
            })}
          </div>
        </div>

        {/* Categories — fades in/out smoothly */}
        <div
          style={{
            opacity: isSearchMode ? 0 : 1,
            maxWidth: isSearchMode ? 0 : 200,
            overflow: isSearchMode || isNavTransitioning ? "hidden" : "visible",
            pointerEvents: isSearchMode ? "none" : "auto",
            transition: isSearchMode
              ? "max-width 300ms cubic-bezier(0.4, 0, 0.2, 1), opacity 200ms ease"
              : "max-width 400ms cubic-bezier(0.0, 0, 0.2, 1) 250ms, opacity 350ms ease 300ms",
          }}
        >
          <CategoriesDropdown />
        </div>
      </nav>
    </div>
  );
};
